'use client';

import { useEffect, useId, useRef, useState } from 'react';
import Link from 'next/link';
import { SafeImage, type ResolvedDefaultImages } from '@/components/common/SafeImage';
import type { HeaderCategoryMenuLink, HeaderCategoryMenuModel } from '@/lib/header-category-menu';

/**
 * Marble.Theme.03 presentation of the header CATEGORY MENU. The model is built once by Storefront Core
 * (lib/header-category-menu.ts: domain-scoped, active-only, DisplayOrder-ordered, localized labels and links, each root and
 * subcategory with its configured image); this file only decides how it looks: a premium two-pane mega-menu under the bar on
 * desktop, an accordion inside the mobile panel. It fetches nothing and builds no URL. Images go through the shared <SafeImage>
 * chain (real image → the domain's category default → general default → bundled placeholder), inside fixed boxes, so a missing or
 * broken image never shows a broken icon and never moves the layout. Its links do not prefetch: opening the menu must not fire
 * one request per category.
 */
type Props = { menu: HeaderCategoryMenuModel; label: string; defaults: ResolvedDefaultImages };

function Chevron({ open }: { open: boolean }) {
  return (
    <svg className="t3-catmenu__chevron" data-open={open ? 'true' : 'false'} width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
      <path d="M1.5 3.5L5 7l3.5-3.5" />
    </svg>
  );
}

/** A fixed-size, cover-fitted thumbnail. Decorative: the category name sits right next to it, so it is hidden from assistive tech. */
function Thumb({ src, defaults, size }: { src: string | null; defaults: ResolvedDefaultImages; size: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`t3-thumb t3-thumb--${size}`} aria-hidden="true">
      <SafeImage src={src} fallbackType="category" defaults={defaults} alt="" className="t3-thumb__img" />
    </span>
  );
}

function ChildLinks({ links, defaults, onNavigate }: { links: HeaderCategoryMenuLink[]; defaults: ResolvedDefaultImages; onNavigate: () => void }) {
  return (
    <ul className="t3-mega__children">
      {links.map((child, index) => (
        <li key={`${child.id}-${index}`}>
          <Link href={child.href} prefetch={false} className="t3-mega__child" onClick={onNavigate}>
            <Thumb src={child.image} defaults={defaults} size="md" />
            <span className="t3-mega__name">{child.label}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Desktop: an entry of the primary navigation that opens the mega-menu. */
export function Theme03CategoryDropdown({ menu, label, defaults }: Props) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const itemRef = useRef<HTMLLIElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <li
      ref={itemRef}
      className="t3-catmenu"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button ref={toggleRef} type="button" className="t3-catmenu__toggle" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((value) => !value)}>
        <span>{label}</span>
        <Chevron open={open} />
      </button>
      {open ? (
        <div id={panelId} className="t3-catmenu__panel t3-mega">
          <div className="t3-mega__body">
            {/*
              DOM order = keyboard order: each root is followed by ITS OWN subcategories, so Tab goes root → its children → next
              root. Visually the selected root's pane sits in the right column (selection follows hover and focus; a click on a
              root simply navigates to it). A root without subcategories shows only its own image and name — no empty panel.
            */}
            <ul className="t3-mega__roots">
              {menu.items.map((item, index) => (
                <li key={`${item.id}-${index}`} className="t3-mega__root" data-selected={index === selected ? 'true' : 'false'} onMouseEnter={() => setSelected(index)} onFocus={() => setSelected(index)}>
                  <Link href={item.href} prefetch={false} className="t3-mega__rootlink" onClick={close}>
                    <Thumb src={item.image} defaults={defaults} size="sm" />
                    <span className="t3-mega__name">{item.label}</span>
                  </Link>
                  <div className="t3-mega__pane">
                    <div className="t3-mega__banner" aria-hidden="true">
                      <Thumb src={item.image} defaults={defaults} size="lg" />
                      <span className="t3-mega__banner-name">{item.label}</span>
                    </div>
                    {item.children.length > 0 ? <ChildLinks links={item.children} defaults={defaults} onNavigate={close} /> : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/** Mobile/tablet: an accordion at the top of the header's disclosure panel — thumbnails included, no desktop layout. */
export function Theme03CategoryAccordion({ menu, label, defaults, onNavigate }: Props & { onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="t3-catmenu-m">
      <button type="button" className="t3-catmenu-m__toggle" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((value) => !value)}>
        <span>{label}</span>
        <Chevron open={open} />
      </button>
      {open ? (
        <div id={panelId} className="t3-catmenu-m__panel">
          <ul className="t3-macc">
            {menu.items.map((item, index) => (
              <li key={`${item.id}-${index}`} className="t3-macc__item">
                <Link href={item.href} prefetch={false} className="t3-macc__root" onClick={onNavigate}>
                  <Thumb src={item.image} defaults={defaults} size="md" />
                  <span className="t3-mega__name">{item.label}</span>
                </Link>
                {item.children.length > 0 ? (
                  <ul className="t3-macc__children">
                    {item.children.map((child, childIndex) => (
                      <li key={`${child.id}-${childIndex}`}>
                        <Link href={child.href} prefetch={false} className="t3-macc__child" onClick={onNavigate}>
                          <Thumb src={child.image} defaults={defaults} size="sm" />
                          <span className="t3-mega__name">{child.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
